/** Cabecera, navegación y pie compartidos por todas las páginas. */

import clsx from 'clsx';
import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';

import { Background } from '@/components/layout/Background';
import { Button } from '@/components/ui';
import { useAuthStore } from '@/stores/authStore';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Inicio', icon: '🏠' },
  { to: '/jugar', label: 'Jugar', icon: '▶️' },
  { to: '/ranking', label: 'Ranking', icon: '🏆' },
  { to: '/estadisticas', label: 'Estadísticas', icon: '📊' },
  { to: '/admin', label: 'Admin', icon: '⚙️', adminOnly: true },
];

export function AppLayout() {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = NAV_ITEMS.filter((item) => !item.adminOnly || user?.is_admin);

  async function handleLogout() {
    await logout();
    navigate('/ingresar', { replace: true });
  }

  return (
    <div className="relative isolate flex min-h-screen flex-col overflow-x-hidden">
      <Background variant="app" />

      <header className="sticky top-0 z-50 border-b-4 border-brand-green bg-white/95 backdrop-blur">
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/dashboard" className="flex items-center gap-3">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-brand-orange to-brand-yellow text-xl"
              aria-hidden
            >
              🕊️
            </span>
            <span className="text-xl font-extrabold text-slate-800">Yachaq Urpi</span>
          </Link>

          <div className="hidden items-center gap-1 md:flex">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  clsx(
                    'rounded-xl px-3 py-2 text-sm font-bold transition-colors',
                    isActive
                      ? 'bg-green-50 text-brand-green-dark'
                      : 'text-slate-600 hover:bg-slate-100',
                  )
                }
              >
                <span aria-hidden>{item.icon}</span> {item.label}
              </NavLink>
            ))}

            <div className="ml-3 flex items-center gap-3 border-l border-slate-200 pl-3">
              <Link
                to="/perfil"
                className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-bold text-slate-700 hover:bg-slate-200"
              >
                <span aria-hidden>🔥</span>
                {user?.streak ?? 0}
                <span className="text-slate-400">·</span>
                {user?.username}
              </Link>
              <Button variant="ghost" size="sm" onClick={() => void handleLogout()}>
                Salir
              </Button>
            </div>
          </div>

          <button
            type="button"
            className="rounded-lg p-2 text-2xl md:hidden"
            aria-label="Abrir menú"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            ☰
          </button>
        </nav>

        {menuOpen && (
          <div className="border-t border-slate-100 bg-white px-4 pb-4 md:hidden">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'block rounded-xl px-3 py-3 text-sm font-bold',
                    isActive ? 'bg-green-50 text-brand-green-dark' : 'text-slate-600',
                  )
                }
              >
                <span aria-hidden>{item.icon}</span> {item.label}
              </NavLink>
            ))}
            <Link
              to="/perfil"
              onClick={() => setMenuOpen(false)}
              className="block rounded-xl px-3 py-3 text-sm font-bold text-slate-600"
            >
              <span aria-hidden>👤</span> Mi perfil
            </Link>
            <Button
              variant="outline"
              fullWidth
              className="mt-2"
              onClick={() => void handleLogout()}
            >
              Cerrar sesión
            </Button>
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-white/70 py-6 text-center text-sm text-slate-500 backdrop-blur">
        <p>
          <strong>Yachaq Urpi</strong> · Paloma que aprende · Aprende quechua jugando
        </p>
      </footer>
    </div>
  );
}
